// #tag: OPPORTUNITIES EXPLORER SCREEN
// #purpose: Recruitment portal displaying active job vacancies, deadline countdowns, qualification filters, and state government opportunities.
import React, { useMemo, useRef, useState } from 'react';
import { Bell, Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { OpportunityCard, OpportunityCountdownCard } from '../components/opportunities/OpportunityCards';
import { PageHeaderSection, FaqSection } from '../components/cms/PageSections';
import { EmptyState, ErrorState, LoadingState } from '../components/states/StateViews';
import type { FaqItem } from '../types/faq';
import { resolveIcon } from '../lib/iconMap';
import { useSearchSounds } from '../hooks/useSearchSounds';
import {
  useClosingSoonOpportunities,
  useFeaturedOpportunities,
  useOpportunities,
  useOpportunityMeta,
  usePageSections,
  useRecentlyClosedOpportunities,
  useTrendingOpportunities,
} from '../hooks/useExamData';

export function OpportunitiesScreen() {
  const navigate = useNavigate();
  const latestRef = useRef<HTMLDivElement>(null);
  const [query, setQuery] = useState('');
  const [opportunityType, setOpportunityType] = useState('all');
  const [qualification, setQualification] = useState('all');
  const [stateFilter, setStateFilter] = useState('all');
  const [windowFilter, setWindowFilter] = useState('all');

  const { data: sectionsData } = usePageSections('opportunities');
  const headerSection = sectionsData?.data?.find((s) => s.sectionType === 'page_header');
  const faqSection = sectionsData?.data?.find((s) => s.sectionType === 'faq');

  const { data: meta } = useOpportunityMeta();
  const { data: featuredData } = useFeaturedOpportunities(4);
  const { data: closingData } = useClosingSoonOpportunities(6);
  const { data: trendingData } = useTrendingOpportunities(4);
  const { data: closedData } = useRecentlyClosedOpportunities(4);

  const listQuery = {
    q: query || undefined,
    opportunityType: opportunityType === 'all' ? undefined : opportunityType,
    qualification: qualification === 'all' ? undefined : qualification,
    state: stateFilter === 'all' ? undefined : stateFilter,
    window: windowFilter === 'all' ? undefined : windowFilter as 'closing_soon' | 'open' | 'new_today' | 'this_week' | 'recently_closed',
    limit: 24,
  };

  const { data, isLoading, isError, refetch } = useOpportunities(listQuery);
  const opportunities = data?.data ?? [];
  const total = data?.pagination.total ?? opportunities.length;
  const { onSearchKeyDown } = useSearchSounds(query, total);

  const categoryCards = useMemo(() => {
    const types = meta?.types ?? [];
    const stats = meta?.categoryStats ?? {};
    return types.map((type) => ({
      ...type,
      stats: stats[type.id] ?? { open: 0, newToday: 0, closingSoon: 0 },
    }));
  }, [meta]);

  const scrollToLatest = () => {
    latestRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  if (isLoading && !data && !meta) {
    return (
      <section className="adaptive-page">
        <LoadingState label="Loading opportunities…" />
      </section>
    );
  }

  if (isError) {
    return (
      <section className="adaptive-page">
        <ErrorState message="Could not load opportunities." onRetry={() => refetch()} playSound={false} />
      </section>
    );
  }

  const faqItems = (faqSection?.configuration as { items?: FaqItem[] })?.items ?? [];

  return (
    <>
      <section className="adaptive-page wfx-opportunities-page">
      {headerSection && (
        <PageHeaderSection
          section={headerSection}
          primaryCta={{ label: 'Browse opportunities', onClick: scrollToLatest }}
          secondaryCta={{ label: 'Set alerts', onClick: () => navigate('/notifications') }}
        />
      )}

      <div className="wfx-search-bar glass-surface">
        <Search size={20} aria-hidden="true" />
        <input
          type="search"
          placeholder="Search jobs, entrance exams, admissions, scholarships…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onSearchKeyDown}
          aria-label="Search opportunities"
        />
      </div>

      <div className="wfx-filter-section">
        <span className="wfx-filter-label">Type</span>
        <div className="wfx-filter-chips" role="group">
          <button type="button" className={`chip-pill ${opportunityType === 'all' ? 'is-active' : ''}`} onClick={() => setOpportunityType('all')}>
            All types
          </button>
          {(meta?.types ?? []).map((type) => (
            <button
              key={type.id}
              type="button"
              className={`chip-pill ${opportunityType === type.id ? 'is-active' : ''}`}
              onClick={() => setOpportunityType(type.id)}
            >
              {type.label}
            </button>
          ))}
        </div>
      </div>

      <div className="wfx-filter-section">
        <span className="wfx-filter-label">Status</span>
        <div className="wfx-filter-chips" role="group">
          {[
            { id: 'all', label: 'All open' },
            { id: 'new_today', label: 'New today' },
            { id: 'this_week', label: 'This week' },
            { id: 'closing_soon', label: 'Closing soon' },
            { id: 'recently_closed', label: 'Recently closed' },
          ].map((item) => (
            <button
              key={item.id}
              type="button"
              className={`chip-pill ${windowFilter === item.id ? 'is-active' : ''}`}
              onClick={() => setWindowFilter(item.id)}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>

      <div className="wfx-filter-section">
        <span className="wfx-filter-label">Qualification</span>
        <div className="wfx-filter-chips" role="group">
          <button type="button" className={`chip-pill ${qualification === 'all' ? 'is-active' : ''}`} onClick={() => setQualification('all')}>
            Any
          </button>
          {(meta?.qualifications ?? []).map((item) => (
            <button
              key={item.id}
              type="button"
              className={`chip-pill ${qualification === item.id ? 'is-active' : ''}`}
              onClick={() => setQualification(item.id)}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>

      {(featuredData?.data.length ?? 0) > 0 && (
        <section className="wfx-opp-section">
          <div className="wfx-opp-section-head">
            <h2>Featured opportunities</h2>
            <p className="wfx-muted">High-demand national recruitments and top entrance exams</p>
          </div>
          <div className="wfx-opp-featured-grid">
            {featuredData!.data.map((opp) => (
              <OpportunityCard key={opp.id} opportunity={opp} featured />
            ))}
          </div>
        </section>
      )}

      {(closingData?.data.length ?? 0) > 0 && (
        <section className="wfx-opp-section">
          <div className="wfx-opp-section-head">
            <h2>Deadline countdown</h2>
            <p className="wfx-muted">Applications closing within 7 days</p>
          </div>
          <div className="wfx-opp-countdown-grid">
            {closingData!.data.map((opp) => (
              <OpportunityCountdownCard key={opp.id} opportunity={opp} />
            ))}
          </div>
        </section>
      )}

      <section className="wfx-opp-section">
        <div className="wfx-opp-section-head">
          <h2>Categories</h2>
        </div>
        <div className="wfx-opp-category-grid">
          {categoryCards.map((cat) => {
            const Icon = resolveIcon(cat.icon, resolveIcon('Briefcase'));
            return (
              <button
                key={cat.id}
                type="button"
                className="wfx-opp-category-card glass-surface"
                onClick={() => setOpportunityType(cat.id)}
              >
                <Icon size={22} aria-hidden="true" />
                <strong>{cat.label}</strong>
                <span className="wfx-muted">{cat.stats.open} open · {cat.stats.closingSoon} closing soon</span>
              </button>
            );
          })}
        </div>
      </section>

      <div className="wfx-filter-section">
        <span className="wfx-filter-label">State</span>
        <div className="wfx-filter-chips" role="group">
          <button type="button" className={`chip-pill ${stateFilter === 'all' ? 'is-active' : ''}`} onClick={() => setStateFilter('all')}>
            All India
          </button>
          {(meta?.states ?? []).filter((s) => s.id !== 'all_india').map((item) => (
            <button
              key={item.id}
              type="button"
              className={`chip-pill ${stateFilter === item.label ? 'is-active' : ''}`}
              onClick={() => setStateFilter(item.label)}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>

      <section ref={latestRef} id="latest" className="wfx-opp-section">
        <div className="wfx-opp-section-head">
          <h2>Latest opportunities</h2>
          <p className="wfx-muted">{total} opportunit{total === 1 ? 'y' : 'ies'} found</p>
        </div>
        {opportunities.length === 0 ? (
          <EmptyState
            title="No opportunities match your filters"
            message="Try another category, qualification, or clear your search."
            illustrationContext="empty.opportunities"
          />
        ) : (
          <div className="wfx-result-grid">
            {opportunities.map((opp) => (
              <OpportunityCard key={opp.id} opportunity={opp} />
            ))}
          </div>
        )}
      </section>

      {(trendingData?.data.length ?? 0) > 0 && (
        <section className="wfx-opp-section">
          <div className="wfx-opp-section-head">
            <h2>Trending</h2>
            <p className="wfx-muted">Most viewed and bookmarked this week</p>
          </div>
          <div className="wfx-result-grid">
            {trendingData!.data.map((opp) => (
              <OpportunityCard key={opp.id} opportunity={opp} />
            ))}
          </div>
        </section>
      )}

      {(closedData?.data.length ?? 0) > 0 && windowFilter !== 'recently_closed' && (
        <section className="wfx-opp-section">
          <div className="wfx-opp-section-head">
            <h2>Recently closed</h2>
            <p className="wfx-muted">For reference — applications that ended recently</p>
          </div>
          <div className="wfx-result-grid">
            {closedData!.data.map((opp) => (
              <OpportunityCard key={opp.id} opportunity={opp} />
            ))}
          </div>
        </section>
      )}

      <section className="wfx-opp-subscribe glass-surface">
        <div>
          <h2>Subscribe to alerts</h2>
          <p className="wfx-muted">Get notified when new opportunities match your qualification, state, or category.</p>
        </div>
        <button type="button" className="primary-action compact" onClick={() => navigate('/settings')}>
          <Bell size={16} /> Notification settings
        </button>
      </section>

      </section>

      <FaqSection
        title={faqSection?.title ?? 'Frequently asked questions'}
        subtitle={faqSection?.subtitle}
        items={faqItems}
      />
    </>
  );
}
